import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { createAgent, updateAgent, getAgentDetail } from "@/services/manage-agent";
import { MainCategory } from "@/types/agent";
import IconCancel from "@/assets/icons/ic-close.svg";
import { CATEGORIES } from "@/constants";
import { usePermissions } from "@/lib/permissions";
import CheckIcon from "@/assets/icons/icon-check.svg";
import IconSave from "@/components/icons/icon-save";

const AddAgentPage = ({ agentId }: { agentId?: string }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { can } = usePermissions();
  const canSubmit = agentId ? can.update("agent_management") : can.create("agent_management");
  const isEditMode = Boolean(agentId);
  const [name, setName] = useState("");
  const [supplier, setSupplier] = useState("");
  const [description, setDescription] = useState("");
  const [solution, setSolution] = useState("");
  const [mainCategory, setMainCategory] = useState<string>("");
  const [applicationArea, setApplicationArea] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  console.log("edit mode", isEditMode);

  const fetchAgent = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    try {
      const res = await getAgentDetail(agentId);
      if (!res.success || !res.data) return;
      const agent = res.data;
      setName(agent.name);
      setSupplier(agent.provider);
      setDescription(agent.short_description);
      setSolution(agent.solution);
      setApplicationArea(agent.application_area);
      setSelectedCategories(agent.categories);
      setMainCategory(agent.main_category);
    } catch {
      toast({ description: "에이전트 정보를 불러오는데 실패했습니다." });
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    fetchAgent();
  }, [fetchAgent]);

  const isFormValid =
    name.trim() &&
    supplier.trim() &&
    description.trim() &&
    solution.trim() &&
    applicationArea.trim() &&
    selectedCategories.length > 0;

  // const toggleCategory = (category: string) => {
  //   setSelectedCategories((prev) =>
  //     prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category],
  //   );
  // };

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        provider: supplier.trim(),
        main_category: mainCategory.trim(),
        short_description: description.trim(),
        solution: solution.trim(),
        categories: selectedCategories.trim(),
        application_area: applicationArea.trim(),
      };
      if (isEditMode && agentId) {
        await updateAgent(agentId, payload);
        toast({ description: "에이전트가 수정되었습니다." });
      } else {
        await createAgent(payload);
        toast({ description: "새 에이전트가 생성되었습니다." });
      }
      navigate("/agents");
    } catch {
      toast({ description: "오류가 발생했습니다. 다시 시도해 주세요." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div className="flex flex-col gap-6" style={{ display: loading ? "none" : "flex" }}>
        {/* Top Bar */}
        <div className="flex items-center justify-end gap-2">
          <Button
            onClick={() => navigate("/agents")}
            className="w-[120px] h-12 px-5 rounded bg-[#EBEBEB] text-[#000000] font-bold text-base "
          >
            <img src={IconCancel} alt="Cancel" />
            취소
          </Button>
          {canSubmit && (
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
              className="h-12 pl-4 pr-5 rounded bg-primary text-[#000000] font-bold text-base gap-2  w-[120px]"
            >
              <IconSave fill="currentColor" />
              {isEditMode ? "저장" : "등록"}
            </Button>
          )}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded flex flex-col gap-6">
          <h2 className="text-xl font-bold text-[#202020]">
            {isEditMode ? "에이전트 수정" : "에이전트 등록"}
          </h2>

          {/* Agent Name */}
          <FormField label="에이전트 이름" required>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 100))}
              onBlur={() => setName(name.trim())}
              placeholder="에이전트 이름 입력..."
            />
          </FormField>

          {/* Supplier */}
          <FormField label="공급사" required>
            <Input
              type="text"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value.slice(0, 50))}
              onBlur={() => setSupplier(supplier.trim())}
              placeholder="공급사 입력..."
            />
          </FormField>

          {/* Short Description */}
          <FormField
            label="한 줄 소개"
            required
            suffix={`${description.length}/200자`}
            hint="(200자 미만)"
          >
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 200))}
              onBlur={() => setDescription(description.trim())}
              placeholder="간단한 소개..."
              className="min-h-[88px]"
            />
          </FormField>

          {/* Solution */}
          <FormField label="솔루션" required>
            <Textarea
              value={solution}
              onChange={(e) => setSolution(e.target.value.slice(0, 2000))}
              onBlur={() => setSolution(solution.trim())}
              placeholder="이 에이전트가 제공하는 솔루션에 대해 설명하세요..."
              className="min-h-[88px]"
            />
          </FormField>

          {/* Major Category */}
          <FormField label="대분류" required>
            <Textarea
              value={mainCategory}
              onChange={(e) => setMainCategory(e.target.value.slice(0, 100))}
              onBlur={() => setMainCategory(mainCategory.trim())}
              placeholder="대분류 입력..."
              className="min-h-[88px]"
            />
          </FormField>

          {/* Categories */}
          <FormField label="상세 기능 카테고리" required>
            <Textarea
              value={selectedCategories}
              onChange={(e) => setSelectedCategories(e.target.value.slice(0, 500))}
              onBlur={() => setSelectedCategories(selectedCategories.trim())}
              placeholder="상세 기능 카테고리 입력..."
              className="min-h-[88px]"
            />
          </FormField>

          {/* Application Area */}
          <FormField label="적용 분야" required>
            <Textarea
              value={applicationArea}
              onChange={(e) => setApplicationArea(e.target.value.slice(0, 500))}
              onBlur={() => setApplicationArea(applicationArea.trim())}
              placeholder="적용 분야 입력..."
              className="min-h-[88px]"
            />
          </FormField>
        </div>
      </div>
    </>
  );
};

function FormField({
  label,
  required,
  hint,
  suffix,
  children,
  cssSuffix,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  suffix?: string;
  children: React.ReactNode;
  cssSuffix?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span className="text-sm text-[#000000]">{label}</span>
          {required && <span className="text-sm text-[#CB3A31]">*</span>}
          {hint && <span className="text-sm text-[#B0B0B0]">{hint}</span>}
        </div>
        {suffix && (
          <span className={`text-sm text-[#B0B0B0] font-bold ${cssSuffix}`}>{suffix}</span>
        )}
      </div>
      {children}
    </div>
  );
}

export default AddAgentPage;
